/**
 * Testable animation status polling logic (no React / timers).
 */

/**
 * @typedef {object} PollResult
 * @property {'success' | 'pending' | 'failure'} kind
 * @property {object} [statusResponse]
 * @property {Error} [error]
 */

/**
 * @typedef {object} PollStepOutcome
 * @property {'completed' | 'continue' | 'client_timeout' | 'error'} type
 * @property {Error} [error]
 */

export const ANIMATION_POLL_INTERVAL_MS = 5000;
export const ANIMATION_CLIENT_MAX_WAIT_MS = 600000;

/**
 * @param {number|null|undefined} startTimeMs
 * @param {number} nowMs
 * @param {number} maxWaitMs
 * @returns {boolean}
 */
export function isClientTimeoutExceeded(startTimeMs, nowMs, maxWaitMs) {
  return startTimeMs != null && nowMs - startTimeMs >= maxWaitMs;
}

/**
 * Interprets a /status response into success, terminal failure, or keep polling.
 * @param {object} statusResponse
 * @param {object} utils
 * @param {(r: object) => string|undefined} utils.normalizeStatus
 * @param {(s: string|undefined) => boolean} utils.isSuccessStatus
 * @param {(s: string|undefined) => boolean} utils.isFailureStatus
 * @param {(r: object, d: string) => string} utils.extractErrorMessage
 * @returns {Omit<PollResult, 'statusResponse'> & { type: 'success' | 'pending' | 'failure' } & { error?: Error }}
 */
export function interpretStatusResponse(statusResponse, utils) {
  const {
    normalizeStatus,
    isSuccessStatus,
    isFailureStatus,
    extractErrorMessage,
  } = utils;
  const normalizedStatus = normalizeStatus(statusResponse);
  if (isSuccessStatus(normalizedStatus)) {
    return { type: "success" };
  }
  if (isFailureStatus(normalizedStatus)) {
    return {
      type: "failure",
      error: new Error(
        extractErrorMessage(
          statusResponse,
          "Animation generation failed"
        )
      ),
    };
  }
  return { type: "pending" };
}

/**
 * Maps a /status body to a result for the polling state machine.
 * @param {object} statusResponse
 * @param {Parameters<typeof interpretStatusResponse>[1]} statusUtils
 * @returns {PollResult}
 */
export function getPollResultFromStatusResponse(
  statusResponse,
  statusUtils
) {
  const row = interpretStatusResponse(statusResponse, statusUtils);
  if (row.type === "success") {
    return { kind: "success", statusResponse };
  }
  if (row.type === "failure" && row.error) {
    return { kind: "failure", statusResponse, error: row.error };
  }
  return { kind: "pending", statusResponse };
}

/**
 * @param {object} input
 * @param {() => boolean} input.checkClientTimeout — when true, polling stops (message already set by caller)
 * @param {() => Promise<PollResult>} input.fetchPollResult
 * @param {(statusResponse: object) => Promise<void>} input.onJobCompleted
 * @param {(error: Error) => void} input.onFailure
 * @returns {Promise<PollStepOutcome>}
 */
export async function runAnimationPollingStep({
  checkClientTimeout,
  fetchPollResult,
  onJobCompleted,
  onFailure,
}) {
  if (checkClientTimeout()) {
    return { type: "client_timeout" };
  }

  let result;
  try {
    result = await fetchPollResult();
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    onFailure(err);
    return { type: "error", error: err };
  }

  if (result.kind === "success") {
    await onJobCompleted(result.statusResponse);
    return { type: "completed" };
  }
  if (result.kind === "failure" && result.error) {
    onFailure(result.error);
    return { type: "error", error: result.error };
  }

  return { type: "continue" };
}
